/** Cash, bank and e-wallet accounts at a glance (M06-01), with transfers. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Pencil } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount, dec, parseAmount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { manilaToday } from '../../lib/queries';
import { Alert, Button, Card, Field, Modal, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';

export interface CashAccountView {
  accountId: string;
  code: string;
  name: string;
  active: boolean;
  systemRole: string | null;
  kind: string;
  bankName: string;
  accountNumberMasked: string | null;
  fundAmount: string | null;
  mappingProfile: string | null;
  balance: string;
  unmatchedLines: number;
  lastReconciled: string | null;
}

export const KIND_LABEL: Record<string, string> = {
  CHECKING: 'Checking',
  SAVINGS: 'Savings',
  CASH_ON_HAND: 'Cash on hand',
  PETTY_CASH: 'Petty cash',
  EWALLET: 'E-wallet',
  CARD_CLEARING: 'Card clearing',
  CHECKS_ON_HAND: 'Checks on hand',
};

export function useCashAccounts() {
  return useQuery({ queryKey: ['cash-accounts'], queryFn: () => api<CashAccountView[]>('/bank-accounts') });
}

export function BankAccountsPage(): JSX.Element {
  const { can } = useAuth();
  const list = useCashAccounts();
  const [editing, setEditing] = useState<CashAccountView | null>(null);
  const [transfer, setTransfer] = useState(false);
  return (
    <>
      <PageHeader
        title="Banking"
        description="Every place money sits. Reconcile against the bank's statement to keep the books equal to real cash."
        actions={can('bank.manage') && <Button variant="secondary" onClick={() => setTransfer(true)}><ArrowLeftRight aria-hidden="true" className="h-4 w-4" /> Transfer funds</Button>}
      />
      <Card>
        {list.isLoading && <Skeleton className="h-48 w-full" />}
        {list.data && (
          <TableWrap>
            <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Account</Th><Th>Type</Th><Th>Bank</Th><Th align="right">Book balance</Th><Th>To match</Th><Th>Last reconciled</Th><Th>{''}</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.map((a) => (
                <tr key={a.accountId} className={a.active ? '' : 'opacity-50'}>
                  <Td><Link className="font-medium text-brand-700 hover:underline dark:text-brand-100" to={`/banking/${a.accountId}`}>{a.code} {a.name}</Link></Td>
                  <Td>{KIND_LABEL[a.kind] ?? a.kind}</Td>
                  <Td className="whitespace-nowrap">{a.bankName || '—'}{a.accountNumberMasked && <span className="ml-2 text-xs text-slate-500">{a.accountNumberMasked}</span>}</Td>
                  <Td align="right"><Money className={dec(a.balance).isNegative() ? 'text-red-600' : ''}>{amount(a.balance)}</Money></Td>
                  <Td>{a.unmatchedLines ? <StatusChip tone="warning">{a.unmatchedLines} lines</StatusChip> : <span className="text-slate-400">—</span>}</Td>
                  <Td className="whitespace-nowrap">{a.lastReconciled ? shortDate(a.lastReconciled) : <span className="text-slate-400">Never</span>}</Td>
                  <Td align="right">{can('bank.manage') && <button className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label={`Edit ${a.name}`} onClick={() => setEditing(a)}><Pencil aria-hidden="true" className="h-4 w-4" /></button>}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
      {editing && <AccountDialog account={editing} onClose={() => setEditing(null)} />}
      {transfer && list.data && <TransferDialog accounts={list.data} onClose={() => setTransfer(false)} />}
    </>
  );
}

function AccountDialog({ account, onClose }: { account: CashAccountView; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState(account.kind);
  const [bankName, setBankName] = useState(account.bankName);
  const [accountNumber, setAccountNumber] = useState('');
  const [fundAmount, setFundAmount] = useState(account.fundAmount ? dec(account.fundAmount).toFixed(2) : '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api(`/bank-accounts/${account.accountId}`, { method: 'PATCH', body: { kind, bankName, ...(accountNumber ? { accountNumber } : {}), fundAmount: fundAmount ? parseAmount(fundAmount) : null } }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['cash-accounts'] }); onClose(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save.'),
  });
  return (
    <Modal title={`${account.code} ${account.name}`} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error}</Alert>}
        <Field label="Type"><select className={inputClass} value={kind} onChange={(e) => setKind(e.target.value)}>{Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
        <Field label="Bank or provider"><input className={inputClass} value={bankName} onChange={(e) => setBankName(e.target.value)} /></Field>
        <Field label="Account number" hint={account.accountNumberMasked ? `On file: ${account.accountNumberMasked}. Type to replace. Stored encrypted.` : 'Stored encrypted; only the last 4 digits are shown.'}><input className={inputClass} value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} autoComplete="off" /></Field>
        {kind === 'PETTY_CASH' && <Field label="Fund amount (imprest)" hint="Replenishment tops the fund back up to this."><input className={`${inputClass} text-right`} inputMode="decimal" value={fundAmount} onChange={(e) => setFundAmount(e.target.value)} /></Field>}
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" busy={save.isPending}>Save</Button></div>
      </form>
    </Modal>
  );
}

export function TransferDialog({ accounts, onClose, preset }: { accounts: CashAccountView[]; onClose: () => void; preset?: { toAccountId?: string; amount?: string; description?: string } }): JSX.Element {
  const queryClient = useQueryClient();
  const [from, setFrom] = useState(accounts.find((a) => a.kind === 'CHECKING')?.accountId ?? '');
  const [to, setTo] = useState(preset?.toAccountId ?? '');
  const [amt, setAmt] = useState(preset?.amount ?? '');
  const [fee, setFee] = useState('');
  const [date, setDate] = useState(manilaToday());
  const [description, setDescription] = useState(preset?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api('/fund-transfers', { method: 'POST', body: { fromAccountId: from, toAccountId: to, amount: parseAmount(amt), fee: fee ? parseAmount(fee) : undefined, date, description } }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['cash-accounts'] }); void queryClient.invalidateQueries({ queryKey: ['petty-cash'] }); onClose(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not transfer.'),
  });
  const opts = accounts.filter((a) => a.active);
  return (
    <Modal title="Transfer funds" description="Between banks, into petty cash, or depositing cash on hand." onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="From" required><select className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)}><option value="">Choose</option>{opts.map((a) => <option key={a.accountId} value={a.accountId}>{a.code} {a.name}</option>)}</select></Field>
          <Field label="To" required><select className={inputClass} value={to} onChange={(e) => setTo(e.target.value)}><option value="">Choose</option>{opts.map((a) => <option key={a.accountId} value={a.accountId}>{a.code} {a.name}</option>)}</select></Field>
          <Field label="Amount" required><input className={`${inputClass} text-right`} inputMode="decimal" value={amt} onChange={(e) => setAmt(e.target.value)} /></Field>
          <Field label="Transfer fee"><input className={`${inputClass} text-right`} inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} /></Field>
          <Field label="Date" required><input type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Description"><input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        </div>
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" busy={save.isPending} disabled={!from || !to || !parseAmount(amt)}>Transfer</Button></div>
      </form>
    </Modal>
  );
}
